import { SagaIterator } from 'redux-saga';
import {
  PapiDeviation,
  PapiRequestDaSharedCommission,
  PapiUser,
} from '@wix/da-papi-types';
import { getDeviationUrl } from '../../DeviationViews/_helpers';
import { Url, USER_PROFILE_SHOP_SUB_PATHS } from '@wix/da-url';
import {
  createCommission,
  changeCommissionStatus,
  changeCommissionStatusSuccess,
  updateCommission,
  toggleCommissionStatus,
  updateSubmittingState,
  updateSubmitErrorString,
  fetchCommission,
  openCommissionEditModal,
} from './actionCreators';
import { isApiError, requestNapi, requestPuppy } from '@wix/da-http-client';
import {
  call,
  all,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { normalize } from '../normalizr';
import { putEntities } from '../entities/actions';
import { deviationSchema } from '../normalizr/schemas/deviation';
import {
  CommissionStatusCodeMap,
  CommissionStatusType,
} from '../../types/commissions';
import { getCommissionById, getIsActive, getIsDeleted } from './selectors';
import { logBiEvent } from '../../biLogger/redux/saga';
import { ResourceType } from '../../types/resource';
import { PureBiData, SubmitContentBiEvent } from '@wix/da-bi/pkg/events';
import { getCurrentUser } from '../../publicSession/selectors';
import noop from '../../utils/noop';
import { popModal, pushModal } from '../../Modals/redux/basicActions';
import { ModalType } from '../../Modals/redux/types';

function fakeResponse(options): any {
  if (process.env.NODE_ENV !== 'production') {
    if (window.doError) {
      return {
        error: 'invalid_request',
        error_description: 'Not logged in.',
        status: 'error',
      };
    }
    return {
      success: true,
    };
  }
}

function* doPapiRequest(isNapi: boolean, endpoint: string, data?: any) {
  return yield call(
    isNapi ? requestNapi : requestPuppy,
    {
      method: 'post',
      url: endpoint,
      data,
    },
    fakeResponse
  );
}

function* createCommissionSaga(action: ReturnType<typeof createCommission>) {
  const {
    title,
    pointsCost: points_cost,
    editorRawState,
    coverImageDeviationId,
  } = action.payload;

  const params = {
    title,
    points_cost,
    cover_image_deviation_id: coverImageDeviationId,
    editorRaw: JSON.stringify(editorRawState),
  };

  const response = yield call(
    doPapiRequest,
    true,
    '/commission/create',
    params
  );
  if (response && response.deviation) {
    yield call(
      logBiEvent,
      PureBiData<SubmitContentBiEvent>({
        evid: 11,
        group_exclusive: false,
        typeid: ResourceType.COMMISSION,
        itemid: response.deviation.deviationId,
      })
    );
    const currentUser: PapiUser = yield select(getCurrentUser);

    window.location.href = `${Url.userShopPath(
      currentUser.username,
      USER_PROFILE_SHOP_SUB_PATHS.COMMISSIONS
    )}?commission-publish-success=1`;
  } else if (response && response.errorCode === 302) {
    window.location.replace(response.errorDetails.redirectUrl);
  } else if (
    response &&
    response.error === 'invalid_request' &&
    response.errorDetails &&
    response.errorDetails.Form
  ) {
    yield put(updateSubmittingState({ submittingState: 'error' }));
    yield put(
      updateSubmitErrorString({
        submitErrorString: `Creation of your new commission failed: ${response.errorDetails.Form}`,
      })
    );
    yield call(
      alert,
      `Creation of your new commission failed: ${response.errorDetails.Form}`
    );
  }
}

export function* changeCommissionStatusSaga(action): SagaIterator {
  const { deviation, status } = action.payload;
  const response = yield call(
    doPapiRequest,
    false,
    '/commission/change_status',
    {
      commissionid: deviation.deviationId,
      status,
    }
  );

  if (response && response.success) {
    const updatedDeviation = { ...deviation };
    updatedDeviation.commission.status =
      CommissionStatusCodeMap[response.data.status];
    const { entities } = normalize(updatedDeviation, deviationSchema);
    yield put(putEntities({ entities }));
    yield put(changeCommissionStatusSuccess(updatedDeviation));
  }
}

export function* toggleCommissionStatusSaga(
  action: ReturnType<typeof toggleCommissionStatus>
): SagaIterator {
  const deviation = action.payload as PapiDeviation;
  const isDeleted = yield select(getIsDeleted, deviation.deviationId);
  if (isDeleted) {
    return;
  }
  const isActive = yield select(getIsActive, deviation.deviationId);
  const status = isActive
    ? CommissionStatusType.DISABLED
    : CommissionStatusType.ACTIVE;
  yield put(changeCommissionStatus({ deviation, status }));
}

export function* updateCommissionSaga(
  action: ReturnType<typeof updateCommission>
): SagaIterator {
  const {
    title,
    pointsCost: points_cost,
    coverImageDeviationId,
    editorRawState,
    commissionid,
    redirectOnSuccess = true,
    onSuccess = noop,
    onError = noop,
  } = action.payload;

  const params = {
    commissionid,
    title,
    cover_image_deviation_id: coverImageDeviationId,
    points_cost,
    editorRaw: JSON.stringify(editorRawState),
  };

  const response = yield call(
    doPapiRequest,
    true,
    '/commission/update',
    params
  );
  if (response && response.deviation) {
    if (redirectOnSuccess) {
      window.location.assign(getDeviationUrl(response.deviation));
    } else {
      const { entities } = normalize(response.deviation, deviationSchema);
      yield put(putEntities({ entities }));
      yield put(updateSubmittingState({ submittingState: 'idle' }));
      yield call(onSuccess, response.deviation);
      return;
    }
  }

  yield put(updateSubmittingState({ submittingState: 'error' }));
  yield put(
    updateSubmitErrorString({
      submitErrorString: 'No deviation in response',
    })
  );
  yield call(onError);
}

export function* fetchCommissionSaga({
  payload: { deviationId, username, onSuccess },
}: ReturnType<typeof fetchCommission>): SagaIterator {
  const result = yield call(
    requestPuppy,
    {
      method: 'get',
      url: 'commission',
      params: {
        commissionid: deviationId,
        username,
        expand: 'deviation.fulltext',
      } as PapiRequestDaSharedCommission,
    },
    fakeResponse
  );
  if (!isApiError(result) && result?.deviation) {
    const { entities } = normalize(result.deviation, deviationSchema);
    yield put(putEntities({ entities }));

    if (onSuccess) {
      const deviation = yield select(getCommissionById, deviationId);
      yield call(onSuccess, deviation);
    }
  }
}

export function* openCommissionEditModalSaga({
  payload: { params },
}: ReturnType<typeof openCommissionEditModal>): SagaIterator {
  const openModal = function* (mergeParams?: any) {
    yield put(
      pushModal(ModalType.COMMISSION, {
        params: {
          ...params,
          ...mergeParams,
        },
        wrapInModal: false,
      })
    );
  };

  if (params.preload && params.deviation) {
    const deviation = params.deviation as PapiDeviation;
    yield put(pushModal(ModalType.LOADING_SCREEN, {}));
    yield put(
      fetchCommission({
        deviationId: deviation.deviationId,
        username: deviation.author?.username ?? '',
        onSuccess: function* (responseDeviation) {
          yield put(popModal());
          yield call(openModal, { deviation: responseDeviation });
        },
      })
    );
  } else {
    yield call(openModal);
  }
}

export default function* saga(): any {
  yield all([
    takeLatest(changeCommissionStatus, changeCommissionStatusSaga),
    takeLatest(toggleCommissionStatus, toggleCommissionStatusSaga),
    takeEvery(updateCommission, updateCommissionSaga),
    takeLatest(createCommission, createCommissionSaga),
    takeLatest(fetchCommission, fetchCommissionSaga),
    takeLatest(openCommissionEditModal, openCommissionEditModalSaga),
  ]);
}
